import React, { useEffect, useMemo, useState } from "react";
import * as S from "./PageWrapper.style";
import { TryForFreeBanner } from "./TryForFreeBanner";
import { Notifications } from "~/components/notifications";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { PlansModal } from "~/components/plans/plansModal";
import { ResumeModal } from "~/components/resumeModal";
import { LimitsModal } from "../limits-modal";
import { useAccount } from "~/hooks/useAccount";
import { useNavigate } from "react-router-dom";
import { Onboarding } from "../onboarding";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { usePlans } from "~/api/billing/queries";
import { paths } from "~/router/paths";
import { IPlan, SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { Userpilot } from "userpilot";
import { useLocation } from "react-router-dom";
import { ModalConnectToStore } from "../connect-to-store/modal";
import { ShowUpgradeModal } from "../upgrade-funnel/showUpgradeModal";
import { AnualPlansModal } from "../plans/anualPlansModal";
import { SubscriptionLockModal } from "~/components/subscription-lock-modal";
import { SubscriptionLockReason } from "~/types/account";
import { Terms } from "~/constants/terms";
import { HELP_CENTER_URL } from "~/constants/sidebar";
import { AliDropPromotionModal } from "../alidrop-promotion-modal";
import { FORCE_SIGN_OUT } from "~/helpers/customEvents";

interface IProps {
  children: React.ReactNode;
}

export const PageWrapper: React.FC<IProps> = ({ children = null }) => {
  const supportedLockReasons: Exclude<SubscriptionLockReason, null>[] = [
    "trial_ended",
    "cancelled_period_ended",
    "subscription_expired",
    "past_due",
    "suspended",
    "billing_status_unknown",
  ];
  const pageLocation = useLocation();
  const { getAccount, account } = useAccount();
  const isSettingsPage = pageLocation.pathname.includes("settings");
  const isSettingsPlansPage = pageLocation.pathname === paths.settings.plans;
  const isSettingsAccountPage = pageLocation.pathname === paths.settings.account;
  const isNotMobile = useMediaQuery("tablet");
  const queryParams = useMemo(() => new URLSearchParams(pageLocation.search), [pageLocation.search]);
  const [isShowingOnboarding, setIsShowingOnboarding] = useState(false);

  const planId = queryParams.get("plan_id");
  const showRating = queryParams.get("show_rating");
  const onboarding = queryParams.get("onboarding");
  const annual = queryParams.get("annual");
  const { data: plans } = usePlans();
  const activePlans = plans?.filter(
    (plan: IPlan) => plan.status === PlanStatus.ACTIVE
  );

  const checkoutPlanFound = useMemo(() => {
    let plan: IPlan | undefined;

    plan = activePlans?.find(
      (p: IPlan) =>
        p.name === "Empire" &&
        p.interval === SubscriptionType.MONTHLY &&
        p.for_winning
    );

    if (!plan) {
      plan = activePlans?.find(
        (p: IPlan) =>
          p.name === "Professional" &&
          p.interval === SubscriptionType.MONTHLY &&
          p.for_winning
      );
    }

    return plan;
  }, [activePlans]);

  const navigate = useNavigate();
  const isDashboardPage = pageLocation.pathname === paths.app.home;
  const subscriptionLockReason = supportedLockReasons.find(
    (reason) => reason === account?.subscription_lock_reason
  );

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  useEffect(() => {
    const handleForceSignOut = () => {
      navigate(paths.auth.login, { replace: true });
    };

    window.addEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    return () => {
      window.removeEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    };
  }, [navigate]);

  useEffect(() => {
    const checkOnboardingCondition = async () => {
      const account = await getAccount();

      if (onboarding && account && !account?.active_subscription) {
        setIsShowingOnboarding(true);
      }

      const searchParams = new URLSearchParams(window.location.search);

      if (annual) {
        searchParams.set("annual", "true");
        navigate(`?${searchParams.toString()}`);
      }

      if (
        account &&
        !account?.active_subscription &&
        !account?.has_subscribed_before &&
        !planId &&
        !showRating
      ) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", account?.signup_origin);
        navigate(`?${searchParams.toString()}`);
      } else if (!!onboarding) {
        setIsShowingOnboarding(false);
        searchParams.delete("onboarding");
        const queryParams = searchParams.toString();
        const newUrl =
          window.location.pathname + (!!queryParams ? `?${queryParams}` : "");
        window.history.replaceState(null, "", newUrl);
      }
    };

    checkOnboardingCondition();
  }, [planId, onboarding, annual]);

  return (
    <S.PageWrapper padding={"24px"} $isDashboard={isDashboardPage}>
      <S.PageHeader>{isNotMobile ? <Notifications /> : null}</S.PageHeader>
      <S.PageMaxWidth>
        {isDashboardPage && <TryForFreeBanner />}
        <>{children}</>
      </S.PageMaxWidth>
      <PlansModal />
      <AliDropPromotionModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <ShowUpgradeModal />

      <ModalConnectToStore />

      <Onboarding
        isShowing={isShowingOnboarding}
        onFinish={() => {
          if (DISABLE_PAYMENTS) {
            setIsShowingOnboarding(false);
          } else {
            navigate(
              paths.checkout.index.replace(
                ":id",
                checkoutPlanFound?.id as string
              )
            );
          }
        }}
      />
      {subscriptionLockReason && !isSettingsPlansPage && !isSettingsAccountPage && !planId ? (
        <SubscriptionLockModal
          isShowing
          reason={subscriptionLockReason}
          onPrimaryAction={() => {
            if (subscriptionLockReason === "suspended") {
              window.open(HELP_CENTER_URL, "_blank");
            } else if (subscriptionLockReason === "past_due" || subscriptionLockReason === "billing_status_unknown") {
              navigate(paths.settings.account);
            } else {
              navigate(paths.settings.plans);
            }
          }}
          onSecondaryAction={() => {
            if (subscriptionLockReason === "suspended") {
              window.open(Terms.terms, "_blank");
            } else if (subscriptionLockReason === "cancelled_period_ended") {
              window.open(HELP_CENTER_URL, "_blank");
            } else if (subscriptionLockReason === "past_due" || subscriptionLockReason === "billing_status_unknown") {
              window.location.reload();
            } else {
              navigate(paths.settings.plans);
            }
          }}
        />
      ) : null}
    </S.PageWrapper>
  );
};

export const PageWrapperFullScreen: React.FC<IProps> = ({
  children = null,
}) => {
  const pageLocation = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  useEffect(() => {
    const handleForceSignOut = () => {
      navigate(paths.auth.login, { replace: true });
    };

    window.addEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    return () => {
      window.removeEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    };
  }, [navigate]);

  return (
    <S.PageWrapperFullscreen>
      <PlansModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <>{children}</>
    </S.PageWrapperFullscreen>
  );
};
